import React, { useState, useEffect } from "react";
import { Table, Form, Spinner, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import classes from "./AvailabilityList.module.css";

const Availability = () => {
  const { t } = useTranslation();
  const [beds, setBeds] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Filter States
  const [floorFilter, setFloorFilter] = useState("");
  const [wardFilter, setWardFilter] = useState("");
  const [bedFilter, setBedFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");

  const fetchBeds = async () => {
    const token = getAuthToken();
    if (!token || token === "EXPIRED") return;

    setIsLoading(true);
    try {
      const apiChar = getApiChara();

      // Fetch Available Beds
      const availableUrl = `${apiChar}/ipd/wardBed/available`;
      // Fetch Active Admissions to get occupied beds
      const activeAdmissionsUrl = `${apiChar}/ipd/admissions/status/ACTIVE`;

      const [availableRes, activeRes] = await Promise.all([
        fetch(availableUrl, { headers: { Authorization: "Bearer " + token } }),
        fetch(activeAdmissionsUrl, { headers: { Authorization: "Bearer " + token } })
      ]);

      if (!availableRes.ok || !activeRes.ok) throw new Error("Failed to fetch bed data");

      const availableData = await availableRes.json();
      const activeData = await activeRes.json();

      const availableBeds = (availableData || []).map(bed => ({ ...bed, status: t("Available") }));

      const occupiedBeds = (activeData || [])
        .filter(adm => adm.wardBed) // Ensure wardBed exists
        .map(adm => ({
          ...adm.wardBed,
          status: t("Occupied"),
          patientName: `${adm.patientId?.firstName} ${adm.patientId?.lastName}`,
          ipdAdmissionId: adm.ipdAdmissionId
        }));

      // Merge and remove duplicates if any (though availability should handle it)
      const allBeds = [...availableBeds, ...occupiedBeds];
      setBeds(allBeds);
    } catch (err) {
      console.error(err);
      toast.error("Error fetching bed availability");
      setBeds([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBeds();
  }, []);

  // Filter Logic
  const filteredBeds = beds.filter(bed => {
    const floorMatch = (bed.floor || "").toLowerCase().includes(floorFilter.toLowerCase());
    const wardMatch = (bed.wardName || "").toLowerCase().includes(wardFilter.toLowerCase());
    const bedMatch = String(bed.bedNumber || "").toLowerCase().includes(bedFilter.toLowerCase());
    const statusMatch = statusFilter === "ALL" || bed.status === t(statusFilter);
    return floorMatch && wardMatch && bedMatch && statusMatch;
  });

  return (
    <div className="container">
      <div className={classes.headerRow}>
        <h2 className={classes.pageTitle}>{t("Bed Availability")}</h2>
      </div>

      <div className="mb-4">
        <Row className="g-2">
          <Col md={3} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-bold">{t("Filter by Floor")}</Form.Label>
              <Form.Control
                type="text"
                list="floorFilterOptions"
                placeholder={t("Search or Select Floor")}
                value={floorFilter}
                className="form-control-sm"
                onChange={(e) => setFloorFilter(e.target.value)}
              />
              <datalist id="floorFilterOptions">
                <option value="First" />
                <option value="Second" />
                <option value="Third" />
                <option value="Fourth" />
                <option value="Fifth" />
              </datalist>
            </Form.Group>
          </Col>
          <Col md={3} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-bold">{t("Filter by Ward")}</Form.Label>
              <Form.Control
                type="text"
                list="wardFilterOptions"
                placeholder={t("Search or Select Ward")}
                value={wardFilter}
                className="form-control-sm"
                onChange={(e) => setWardFilter(e.target.value)}
              />
              <datalist id="wardFilterOptions">
                <option value="General" />
                <option value="Private" />
                <option value="Semi-Private" />
                <option value="ICU" />
                <option value="CCU" />
                <option value="Pediatric" />
                <option value="Maternity" />
              </datalist>
            </Form.Group>
          </Col>
          <Col md={3} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-bold">{t("Filter by Bed No")}</Form.Label>
              <Form.Control
                type="text"
                placeholder={t("Search bed number...")}
                value={bedFilter}
                className="form-control-sm"
                onChange={(e) => setBedFilter(e.target.value)}
              />
            </Form.Group>
          </Col>
          <Col md={3} sm={6}>
            <Form.Group>
              <Form.Label className="small fw-bold">{t("Filter by Status")}</Form.Label>
              <Form.Select
                value={statusFilter}
                className="form-select-sm"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="ALL">{t("ALL")}</option>
                <option value="Available">{t("Available")}</option>
                <option value="Occupied">{t("Occupied")}</option>
              </Form.Select>
            </Form.Group>
          </Col>
        </Row>
      </div>

      {/* 🔹 Table Section */}
      <div className="printData boxes table-responsive">
        <Table striped bordered hover responsive>
          <thead>
            <tr>
              <th>{t("ID")}</th>
              <th>{t("Floor")}</th>
              <th>{t("Ward Name")}</th>
              <th>{t("Bed No")}</th>
              <th>{t("Description")}</th>
              <th>{t("Status")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="6" className="text-center">
                  <Spinner animation="border" size="sm" /> {t("Loading availability...")}
                </td>
              </tr>
            ) : filteredBeds.length > 0 ? (
              filteredBeds.map((bed) => (
                <tr key={bed.id}>
                  <td>{bed.id}</td>
                  <td>{bed.floor || "-"}</td>
                  <td>{bed.wardName || "-"}</td>
                  <td>{bed.bedNumber || "-"}</td>
                  <td>{bed.description || "-"}</td>
                  <td className={bed.status === "Available" ? classes.available : classes.occupied}>
                    {bed.status}
                    {bed.status === "Occupied" && bed.patientName && (
                      <div className="small text-muted" style={{ fontSize: "10px" }}>
                        ({bed.patientName})
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="6" className="text-center">
                  {t("No matching available beds found")}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

export default Availability;
